import { X, Check } from 'lucide-react';
import type { ReactNode } from 'react';

import type {
  ArrayFilterKey,
  ScheduleFilters,
} from '../../../hooks/schedule/useScheduleFilters';

interface FilterOptions {
  locations: string[];
  sports: string[];
  skillLevels: string[];
  coaches: string[];
  ageBrackets: string[];
}

interface ScheduleFilterDrawerProps {
  open: boolean;
  filters: ScheduleFilters;
  options: FilterOptions;
  activeCount: number;
  onClose: () => void;
  onToggle: (key: ArrayFilterKey, value: string) => void;
  onSetAgeBracket: (value: string | undefined) => void;
  onClear: () => void;
}

function CheckboxRow({
  label,
  checked,
  onClick,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-[#EBFFEE] hover:bg-[#363232] transition-colors"
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
          checked
            ? 'bg-[#9B1421] border-[#9B1421]'
            : 'bg-[#221F1F] border-[#504A4A]'
        }`}
      >
        {checked && <Check size={11} className="text-white" strokeWidth={3} />}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function FilterGroup({
  title,
  children,
  empty,
}: {
  title: string;
  children: ReactNode;
  empty?: boolean;
}) {
  return (
    <div>
      <h3 className="text-[10px] uppercase font-semibold text-[#A9A3A3] tracking-wider mb-1.5">
        {title}
      </h3>
      {empty ? (
        <p className="px-2 py-1 text-xs text-[#504A4A] italic">No options</p>
      ) : (
        <div className="space-y-0.5">{children}</div>
      )}
    </div>
  );
}

export function ScheduleFilterDrawer({
  open,
  filters,
  options,
  activeCount,
  onClose,
  onToggle,
  onSetAgeBracket,
  onClear,
}: ScheduleFilterDrawerProps) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden={!open}
      />

      {/* Drawer */}
      <aside
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-sm bg-[#1A1717] border-l border-[#363232] shadow-2xl flex flex-col transition-transform duration-200 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#363232] shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[#EBFFEE] font-heading text-lg font-bold">Filters</h2>
            {activeCount > 0 && (
              <span className="text-[10px] bg-[#9B1421] text-white px-1.5 py-0.5 rounded-full font-semibold">
                {activeCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          <FilterGroup title="Location" empty={options.locations.length === 0}>
            {options.locations.map((v) => (
              <CheckboxRow
                key={v}
                label={v}
                checked={filters.location?.includes(v) ?? false}
                onClick={() => onToggle('location', v)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Sport" empty={options.sports.length === 0}>
            {options.sports.map((v) => (
              <CheckboxRow
                key={v}
                label={v}
                checked={filters.sport?.includes(v) ?? false}
                onClick={() => onToggle('sport', v)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Skill Level" empty={options.skillLevels.length === 0}>
            {options.skillLevels.map((v) => (
              <CheckboxRow
                key={v}
                label={v}
                checked={filters.skillLevel?.includes(v) ?? false}
                onClick={() => onToggle('skillLevel', v)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Coach" empty={options.coaches.length === 0}>
            {options.coaches.map((v) => (
              <CheckboxRow
                key={v}
                label={v}
                checked={filters.coach?.includes(v) ?? false}
                onClick={() => onToggle('coach', v)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Age Bracket" empty={options.ageBrackets.length === 0}>
            {options.ageBrackets.map((v) => (
              <CheckboxRow
                key={v}
                label={v}
                checked={filters.ageBracket === v}
                onClick={() => onSetAgeBracket(filters.ageBracket === v ? undefined : v)}
              />
            ))}
          </FilterGroup>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[#363232] shrink-0">
          <button
            onClick={onClear}
            disabled={activeCount === 0}
            className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Clear all
          </button>
          <button
            onClick={onClose}
            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </aside>
    </>
  );
}
